import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  ShoppingCart,
  Sell,
  MonetizationOn,
  Add,
  Delete,
  AccountBalanceWallet,
  Edit,
  Save,
  Cancel,
  InfoOutlined,
} from '@mui/icons-material';
import { Transaction } from '../types';
import { useNotification } from '../context/NotificationContext';
import { StockNameManager } from '../services/stockNameManager';
import { DecimalHelper } from '../utils/decimalHelper';
import { parseQuantity, parsePrice, validateTransactionPrice, maxPriceDecimals } from '../utils/formParsers';
import { toLocalDateString } from '../utils/csvUtils';

interface EditTransactionDialogProps {
  open: boolean;
  transaction: Transaction | null;
  onClose: () => void;
  onSave: (updatedTransaction: Transaction) => Promise<void>;
  onDelete: (transactionId: string) => Promise<void>;
  isSubmitting?: boolean;
}

interface FormData {
  symbol: string;
  stockName: string;
  type: 'buy' | 'sell' | 'dividend' | 'cash_deposit' | 'cash_withdrawal' | 'stock_increase' | 'stock_decrease';
  quantity: string;
  price: string;
  fees: string;
  taxes: string;
  total: string;
  adjustmentAmount: string;
  adjustmentReason: string;
  transactionDate: string;
  notes: string;
}

const EditTransactionDialog: React.FC<EditTransactionDialogProps> = ({
  open,
  transaction,
  onClose,
  onSave,
  onDelete,
}) => {
  const { showSuccess, showError } = useNotification();
  const [form, setForm] = useState<FormData>({
    symbol: '',
    stockName: '',
    type: 'buy',
    quantity: '',
    price: '',
    fees: '',
    taxes: '',
    total: '',
    adjustmentAmount: '',
    adjustmentReason: '',
    transactionDate: '',
    notes: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // FIN-05:追蹤使用者手動修改過的計算欄位,自動重算時不覆寫
  const manualEditsRef = React.useRef<Set<'fees' | 'taxes' | 'total'>>(new Set());

  // Initialize form data when transaction changes
  useEffect(() => {
    if (transaction) {
      const transactionDate = transaction.transactionDate
        ? new Date(transaction.transactionDate)
        : transaction.timestamp;

      // UI-06:本地時區日期(toISOString 在台灣凌晨會差一天)
      manualEditsRef.current = new Set();
      setForm({
        symbol: transaction.symbol || '',
        stockName: transaction.stockName || StockNameManager.getStockName(transaction.symbol),
        type: transaction.type,
        quantity: transaction.quantity?.toString() || '',
        price: transaction.price?.toString() || '',
        fees: transaction.fees?.toString() || '',
        taxes: transaction.taxes?.toString() || '',
        total: transaction.total?.toString() || '',
        adjustmentAmount: transaction.adjustmentAmount?.toString() || '',
        adjustmentReason: transaction.adjustmentReason || '',
        transactionDate: transactionDate ? toLocalDateString(transactionDate) : '',
        notes: transaction.notes || '',
      });
    }
  }, [transaction]);

  /**
   * 依表單現值重算費用/稅金/總額(FIN-05)
   * 以傳入的 form 計算(在 setForm(prev => ...) 內呼叫,讀取最新值),
   * 取代原本以 setTimeout 捕獲 stale form 的寫法(快速輸入時會儲存到舊值算的總額)
   */
  const applyAutoCalculations = (currentForm: FormData): FormData => {
    if (!currentForm.price || !currentForm.quantity) return currentForm;

    // FIN-06:以 Number 系解析(parseInt 對 NaN/科學記號/小數的行為不正確)
    // 股利允許至小數 8 位(公告值),其餘交易 2 位
    const price = parsePrice(currentForm.price, maxPriceDecimals(currentForm.type));
    const quantity = parseQuantity(currentForm.quantity);
    if (price === null || quantity === null) return currentForm;

    const tradeAmount = DecimalHelper.calculateTradeAmount(quantity, price);

    let fees = 0;
    let taxes = 0;
    let total = 0;

    // Calculate fees using Taiwan stock exchange rules
    if (currentForm.type === 'dividend') {
      fees = 10; // 股利匯費固定 10 元
    } else if (!['stock_increase', 'stock_decrease', 'cash_deposit', 'cash_withdrawal'].includes(currentForm.type)) {
      fees = DecimalHelper.calculateBrokerageFee(tradeAmount); // 無條件捨去
    }

    // Calculate taxes using Taiwan stock exchange rules
    if (currentForm.type === 'sell') {
      taxes = DecimalHelper.calculateTransactionTax(
        tradeAmount,
        DecimalHelper.getTransactionTaxRate(
          currentForm.symbol,
          currentForm.transactionDate ? new Date(currentForm.transactionDate) : undefined,
        ),
      ); // ETF 0.1%/債券ETF停徵/股票 0.3%(依交易日期判斷),小數無條件捨去(券商實務)
    } else if (currentForm.type === 'dividend') {
      taxes = DecimalHelper.calculateSupplementaryPremium(tradeAmount); // 二代健保費
    }

    // Calculate total
    if (currentForm.type === 'buy') {
      total = tradeAmount + fees;
    } else if (currentForm.type === 'sell') {
      total = tradeAmount - fees - taxes;
    } else if (currentForm.type === 'dividend') {
      total = tradeAmount - fees - taxes;
    } else if (['cash_deposit', 'cash_withdrawal'].includes(currentForm.type)) {
      total = Math.abs(tradeAmount);
    } else {
      // stock_increase, stock_decrease
      total = 0;
    }

    // 使用者手動修改過的欄位不覆寫
    const manual = manualEditsRef.current;
    return {
      ...currentForm,
      fees: manual.has('fees') ? currentForm.fees : fees.toString(),
      taxes: manual.has('taxes') ? currentForm.taxes : taxes.toString(),
      total: manual.has('total') ? currentForm.total : Math.abs(total).toString(),
    };
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    if (field === 'fees' || field === 'taxes' || field === 'total') {
      manualEditsRef.current.add(field);
    }

    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Auto-calculate fees and taxes when changing price/quantity/type
      if (['price', 'quantity', 'type'].includes(field)) {
        return applyAutoCalculations(next);
      }
      return next;
    });
  };

  const validateForm = (): string | null => {
    if (!form.symbol.trim() && !['cash_deposit', 'cash_withdrawal'].includes(form.type)) {
      return '請輸入股票代號';
    }

    // FIN-06:parseQuantity 對 NaN / 科學記號 / 小數股數皆正確處理
    if (parseQuantity(form.quantity) === null) {
      return '請輸入有效的股數(正整數)';
    }

    // 價格驗證(共用規則:股票異動可填 0/留空,一般交易須 > 0)
    const priceError = validateTransactionPrice(form.price, form.type);
    if (priceError) return priceError;

    if (!form.transactionDate) {
      return '請選擇交易日期';
    }

    return null;
  };

  const handleSave = async () => {
    const error = validateForm();
    if (error) {
      showError(error);
      return;
    }

    if (!transaction) return;

    setLoading(true);
    try {
      const quantity = parseQuantity(form.quantity) ?? 0;
      const price = parsePrice(form.price, maxPriceDecimals(form.type)) ?? 0;

      // 創建完整的 Transaction 對象
      const updatedTransaction: Transaction = {
        ...transaction, // 保留原有屬性（id, accountId, timestamp 等）
        symbol: form.symbol.toUpperCase(),
        stockName: form.stockName || StockNameManager.getStockName(form.symbol),
        type: form.type,
        quantity,
        price,
        fees: parseFloat(form.fees) || 0,
        taxes: parseFloat(form.taxes) || 0,
        total: parseFloat(form.total) || 0,
        adjustmentAmount: parseFloat(form.adjustmentAmount) || 0,
        adjustmentReason: form.adjustmentReason || undefined,
        transactionDate: new Date(form.transactionDate),
        notes: form.notes,
      };

      await onSave(updatedTransaction);
      showSuccess('交易記錄更新成功！');
      onClose();
    } catch (error) {
      showError(`更新交易記錄失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!transaction) return;
    
    setLoading(true);
    try {
      await onDelete(transaction.id);
      showSuccess('交易記錄刪除成功！');
      setDeleteConfirmOpen(false);
      onClose();
    } catch (error) {
      showError(`刪除交易記錄失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'buy': return <ShoppingCart sx={{ color: '#4caf50' }} />;
      case 'sell': return <Sell sx={{ color: '#f44336' }} />;
      case 'dividend': return <MonetizationOn sx={{ color: '#ff9800' }} />;
      case 'stock_increase': return <Add sx={{ color: '#4caf50' }} />;
      case 'stock_decrease': return <Delete sx={{ color: '#f44336' }} />;
      case 'cash_deposit': return <AccountBalanceWallet sx={{ color: '#4caf50' }} />;
      case 'cash_withdrawal': return <AccountBalanceWallet sx={{ color: '#f44336' }} />;
      default: return <Edit />;
    }
  };

  const getTransactionTypeName = (type: string) => {
    switch (type) {
      case 'buy': return '買入';
      case 'sell': return '賣出';
      case 'dividend': return '股利';
      case 'stock_increase': return '股票增加';
      case 'stock_decrease': return '股票減少';
      case 'cash_deposit': return '存入資金';
      case 'cash_withdrawal': return '提取資金';
      default: return '交易';
    }
  };

  if (!transaction) return null;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getTransactionTypeIcon(form.type)}
            <Box>
              <Typography variant="h6" fontWeight="bold">
                編輯交易記錄
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getTransactionTypeName(form.type)} - {form.symbol || '資金調整'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'grid', gap: 3 }}>
            {/* 基本資訊 */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                基本資訊
              </Typography>
              
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                {/* 股票代號 */}
                {!['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
                  <TextField
                    fullWidth
                    label="股票代號"
                    value={form.symbol}
                    onChange={(e) => handleInputChange('symbol', e.target.value.toUpperCase())}
                    placeholder="例如：2330"
                  />
                )}
                
                {/* 股票名稱 */}
                {!['cash_deposit', 'cash_withdrawal'].includes(form.type) && (
                  <TextField
                    fullWidth
                    label="股票名稱"
                    value={form.stockName}
                    onChange={(e) => handleInputChange('stockName', e.target.value)}
                    placeholder="股票名稱（可選）"
                  />
                )}
                
                {/* 交易類型 */}
                <TextField
                  fullWidth
                  select
                  label="交易類型"
                  value={form.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                >
                  <MenuItem value="buy">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ShoppingCart sx={{ color: '#4caf50', fontSize: 20 }} />
                      買入
                    </Box>
                  </MenuItem>
                  <MenuItem value="sell">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Sell sx={{ color: '#f44336', fontSize: 20 }} />
                      賣出
                    </Box>
                  </MenuItem>
                  <MenuItem value="dividend">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MonetizationOn sx={{ color: '#ff9800', fontSize: 20 }} />
                      股利
                    </Box>
                  </MenuItem>
                  <MenuItem value="stock_increase">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Add sx={{ color: '#4caf50', fontSize: 20 }} />
                      股票增加
                    </Box>
                  </MenuItem>
                  <MenuItem value="stock_decrease">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Delete sx={{ color: '#f44336', fontSize: 20 }} />
                      股票減少
                    </Box>
                  </MenuItem>
                  <MenuItem value="cash_deposit">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceWallet sx={{ color: '#4caf50', fontSize: 20 }} />
                      存入資金
                    </Box>
                  </MenuItem>
                  <MenuItem value="cash_withdrawal">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceWallet sx={{ color: '#f44336', fontSize: 20 }} />
                      提取資金
                    </Box>
                  </MenuItem>
                </TextField>
                
                {/* 交易日期 */}
                <TextField
                  fullWidth
                  label="交易日期"
                  type="date"
                  value={form.transactionDate}
                  onChange={(e) => handleInputChange('transactionDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
            
            <Divider />
            
            {/* 交易詳情 */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                交易詳情
              </Typography>
              
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                {/* 股數 */}
                <TextField
                  fullWidth
                  label="股數"
                  type="number"
                  value={form.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  helperText={['cash_deposit', 'cash_withdrawal'].includes(form.type) ? '資金調整時固定為 1' : ''}
                />
                
                {/* 價格 */}
                <TextField
                  fullWidth
                  label="價格"
                  type="number"
                  value={form.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  inputProps={form.type === 'dividend'
                    ? { min: 0, step: 'any' } // 每股股利公告可達小數 8 位
                    : { min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText={['stock_increase', 'stock_decrease'].includes(form.type) ? '股票異動價格為 0' : ''}
                />
                
                {/* 手續費 */}
                <TextField
                  fullWidth
                  label="手續費"
                  type="number"
                  value={form.fees}
                  onChange={(e) => handleInputChange('fees', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                
                {/* 交易稅 */}
                <TextField
                  fullWidth
                  label="交易稅"
                  type="number"
                  value={form.taxes}
                  onChange={(e) => handleInputChange('taxes', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                
                {/* 總金額 */}
                <TextField
                  fullWidth
                  label="總金額"
                  type="number"
                  value={form.total}
                  onChange={(e) => handleInputChange('total', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                />
                
                {/* 調整總額 */}
                <TextField
                  fullWidth
                  label="調整總額"
                  type="number"
                  value={form.adjustmentAmount}
                  onChange={(e) => handleInputChange('adjustmentAmount', e.target.value)}
                  inputProps={{ step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  helperText="正值表示增加，負值表示減少"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 152, 0, 0.04)',
                      borderColor: 'rgba(255, 152, 0, 0.2)',
                    },
                  }}
                />
                
                {/* 調整原因 */}
                <TextField
                  fullWidth
                  label="調整原因"
                  value={form.adjustmentReason}
                  onChange={(e) => handleInputChange('adjustmentReason', e.target.value)}
                  placeholder="調整金額的原因說明（可選）"
                  disabled={!form.adjustmentAmount || parseFloat(form.adjustmentAmount) === 0}
                  sx={{ 
                    gridColumn: '1 / -1',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: !form.adjustmentAmount || parseFloat(form.adjustmentAmount) === 0 ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 152, 0, 0.04)',
                    }
                  }}
                />
                
                {/* 備註 */}
                <TextField
                  fullWidth
                  label="備註"
                  value={form.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="交易備註（可選）"
                  sx={{ gridColumn: '1 / -1' }}
                />
              </Box>
            </Box>
            {/* 預覽資訊 */}
            {form.quantity && form.price && (
              <>
                <Divider />
                <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 1, border: '1px solid rgba(25, 118, 210, 0.12)' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
                    📋 交易預覽
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
                    <Typography variant="body2">
                      成交價金：${ (parseInt(form.quantity || '0') * parseFloat(form.price || '0')).toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      手續費：${parseFloat(form.fees || '0').toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      交易稅：${parseFloat(form.taxes || '0').toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: form.type === 'buy' ? 'error.main' : 'success.main' }}>
                      {form.type === 'buy' ? '支出' : '收入'}：${Math.abs(parseFloat(form.total || '0')).toLocaleString()}
                    </Typography>
                    {form.adjustmentAmount && parseFloat(form.adjustmentAmount) !== 0 && (
                      <Tooltip title={`調整金額: ${parseFloat(form.adjustmentAmount) >= 0 ? '+' : ''}${parseFloat(form.adjustmentAmount).toLocaleString()}${form.adjustmentReason ? ` (${form.adjustmentReason})` : ''}`}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary',
                            cursor: 'help',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <InfoOutlined sx={{ fontSize: 14 }} />
                          含調整
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={loading}
          >
            刪除
          </Button>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={onClose}
            disabled={loading}
          >
            取消
          </Button>
          
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSave}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? '儲存中...' : '儲存變更'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 刪除確認對話框 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete color="error" />
          確認刪除交易記錄
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            此操作無法復原，刪除後將重新計算帳戶餘額和持股狀況。
          </Alert>
          
          <Typography variant="body1">
            確定要刪除以下交易記錄嗎？
          </Typography>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">交易類型：{getTransactionTypeName(form.type)}</Typography>
            <Typography variant="body2" color="text.secondary">股票代號：{form.symbol || '資金調整'}</Typography>
            <Typography variant="body2" color="text.secondary">股數：{form.quantity}</Typography>
            <Typography variant="body2" color="text.secondary">價格：${form.price}</Typography>
            <Typography variant="body2" color="text.secondary">日期：{form.transactionDate}</Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={loading}
          >
            取消
          </Button>
          
          <Button
            variant="contained"
            color="error"
            startIcon={loading ? <CircularProgress size={20} /> : <Delete />}
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? '刪除中...' : '確認刪除'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditTransactionDialog;
